import { useState, useEffect } from 'react'
import AdminSidebar from '../../components/admin/AdminSidebar'
import AdminHeader from '../../components/admin/AdminHeader'
import { useGet } from '../../utils/apiComponent/useApi'
import { endpoints } from '../../utils/endpoints'
import { Button } from '../../components/ui/button'
import { Badge } from '../../components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar'
import {
  MessageSquare,
  Trash2,
  Pin,
  Eye,
  Search,
  Filter,
  Loader2
} from 'lucide-react'
import { api } from '../../utils/apiComponent/apiClient'
import { showErrorToast, showSuccessToast } from '../../utils/toast'
import { useTranslation } from '../../i18n'

export default function CommentsManagementPage() {
  const { tObj, language } = useTranslation()
  const [comments, setComments] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterCourse, setFilterCourse] = useState('')
  const [filterPinned, setFilterPinned] = useState('all')
  const [courses, setCourses] = useState([])
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  // Charger les cours pour le filtre
  useEffect(() => {
    const loadCourses = async () => {
      try {
        const data = await api.get(endpoints.courses, { isPublicRoute: true })
        const coursesList = Array.isArray(data) ? data : (data?.courses || data?.data?.courses || [])
        setCourses(coursesList)
      } catch (error) {
        console.error('Error loading courses:', error)
      }
    }
    loadCourses()
  }, [])

  // Charger les commentaires
  const loadComments = async (pageNum = 1) => {
    // Si aucun cours n'est sélectionné, ne pas charger les commentaires
    if (!filterCourse) {
      setComments([])
      setLoading(false)
      setPage(1)
      setTotalPages(1)
      return
    }

    setLoading(true)
    try {
      // Construire les paramètres de requête avec le courseId requis
      const params = new URLSearchParams({
        courseId: filterCourse,
        page: pageNum.toString(),
        limit: '50',
        sortBy: 'recent'
      })
      
      const data = await api.get(`${endpoints.getComments}?${params.toString()}`, {
        isPublicRoute: true
      })
      
      let filteredComments = data.data || []
      
      // Filtrer par terme de recherche
      if (searchTerm) {
        filteredComments = filteredComments.filter(comment =>
          comment.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
          comment.user?.name?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      }
      
      // Filtrer par épinglé
      if (filterPinned === 'pinned') {
        filteredComments = filteredComments.filter(comment => comment.isPinned)
      } else if (filterPinned === 'unpinned') {
        filteredComments = filteredComments.filter(comment => !comment.isPinned)
      }
      
      setComments(filteredComments)
      setTotalPages(data.totalPages || 1)
      setPage(pageNum)
    } catch (error) {
      console.error('Error loading comments:', error)
      showErrorToast({ message: 'Erreur lors du chargement des commentaires' })
      setComments([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (filterCourse) {
      loadComments(1)
    } else {
      setComments([])
      setLoading(false)
    }
  }, [filterCourse, filterPinned])

  // Supprimer un commentaire
  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce commentaire ?')) {
      return
    }

    try {
      await api.delete(endpoints.deleteComment(commentId))
      showSuccessToast({ message: 'Commentaire supprimé avec succès' })
      await loadComments(page)
    } catch (error) {
      showErrorToast({ message: 'Erreur lors de la suppression du commentaire' })
    }
  }

  // Épingler/Désépingler
  const handleTogglePin = async (commentId) => {
    try {
      await api.post(endpoints.pinComment(commentId))
      showSuccessToast({ message: 'Commentaire épinglé/désépinglé avec succès' })
      await loadComments(page)
    } catch (error) {
      showErrorToast({ message: 'Erreur lors de l\'épinglage du commentaire' })
    }
  }

  // Voir le cours
  const handleViewCourse = (courseId) => {
    window.open(`/courses/${courseId}`, '_blank')
  }

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return dateString
    }
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <AdminSidebar />
      <div className="flex-1 ml-64 p-8 overflow-y-auto">
        <AdminHeader
          title="Gestion des commentaires"
          subtitle="Modérer et gérer tous les commentaires des cours"
        />

        {/* Filtres et recherche */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Rechercher un commentaire..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value)
                  if (filterCourse) {
                    loadComments(1)
                  }
                }}
                disabled={!filterCourse}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835] disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>
            <select
              value={filterCourse}
              onChange={(e) => {
                setFilterCourse(e.target.value)
                setPage(1)
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
              required
            >
              <option value="">Sélectionner un cours</option>
              {courses.map((course) => (
                <option key={course.id} value={course.id}>
                  {course.title}
                </option>
              ))}
            </select>
            <select
              value={filterPinned}
              onChange={(e) => {
                setFilterPinned(e.target.value)
                setPage(1)
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
            >
              <option value="all">Tous</option>
              <option value="pinned">Épinglés</option>
              <option value="unpinned">Non épinglés</option>
            </select>
          </div>
        </div>

        {/* Liste des commentaires */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {!filterCourse ? (
            <div className="text-center py-12">
              <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg font-medium mb-2">Sélectionnez un cours pour voir les commentaires</p>
              <p className="text-gray-400 text-sm">Veuillez choisir un cours dans le menu déroulant ci-dessus</p>
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-[#141835]" />
            </div>
          ) : comments.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Aucun commentaire trouvé pour ce cours</p>
            </div>
          ) : (
            <>
              <div className="w-full overflow-x-auto">
                <table className="min-w-[1100px] divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Auteur
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Commentaire
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cours
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Statistiques
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50 shadow-[-8px_0_12px_-12px_rgba(0,0,0,0.35)]">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {comments.map((comment) => (
                    <tr key={comment.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <Avatar className="w-10 h-10">
                            <AvatarImage src={comment.user?.avatar} />
                            <AvatarFallback>
                              {comment.user?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U'}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {comment.user?.name || 'Utilisateur inconnu'}
                            </div>
                            {comment.user?.role && (
                              <Badge variant="outline" className="text-xs mt-1">
                                {comment.user.role}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-md">
                          <p className="line-clamp-2">{comment.content}</p>
                          {comment.isEdited && (
                            <span className="text-xs text-gray-500">(modifié)</span>
                          )}
                          {comment.parentId && (
                            <Badge variant="outline" className="text-xs mt-1">
                              Réponse
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col gap-1">
                          <span className="text-sm font-medium text-gray-900">
                            {courses.find(c => c.id === comment.courseId)?.title || 'Cours inconnu'}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewCourse(comment.courseId)}
                            className="text-blue-600 hover:text-blue-800 p-0 h-auto"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Voir le cours
                          </Button>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-600">
                          <div className="flex items-center gap-4">
                            <span>❤️ {comment.likesCount || 0}</span>
                            <span>💬 {comment.repliesCount || 0}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(comment.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium sticky right-0 bg-white shadow-[-8px_0_12px_-12px_rgba(0,0,0,0.35)]">
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleTogglePin(comment.id)}
                            className={comment.isPinned ? 'text-yellow-600 hover:text-yellow-700' : 'text-gray-600 hover:text-yellow-600'}
                            title={comment.isPinned ? 'Désépingler' : 'Épingler'}
                          >
                            <Pin className={`w-4 h-4 ${comment.isPinned ? 'fill-current' : ''}`} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteComment(comment.id)}
                            className="text-red-600 hover:text-red-700"
                            title="Supprimer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-6 py-4 border-t">
                  <div className="text-sm text-gray-600">
                    Page {page} sur {totalPages}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      onClick={() => loadComments(page - 1)}
                      disabled={page === 1}
                    >
                      Précédent
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => loadComments(page + 1)}
                      disabled={page >= totalPages}
                    >
                      Suivant
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

